/**
 * Détection des lignes « avoir » dans les listes (facture / avoir partagent parfois le même flux UI).
 * Priorité : type AVOIR, flag API `statut.avoir`, puis titres historiques avoir.
 * Ne pas utiliser document_original_id seul : une facture convertie depuis BL/commande a cet id
 * et serait sinon exclue du tableau alors que le compteur API (total_items) reste correct.
 */

export function isStatutMarqueAvoir(statut: any): boolean {
    if (statut == null) {
        return false;
    }
    const v = statut.avoir;
    return v === true || v === 1 || v === '1';
}

const LEGACY_AVOIR_TITRES = ['Utilisé', 'Non utilisé', 'Partiellement utilisé'];

/** Répare les titres UTF-8 mal décodés (ex. « Non pay├® » / « Non pay????? » → « Non payé »). */
export function repairStatutTitreEncoding(status: string): string {
    let raw = String(status || '').trim();
    if (!raw) {
        return raw;
    }
    raw = raw.replace(/\uFFFD+/g, '?');
    if (/^non\s*pay\?+/i.test(raw) || /^non\s*paye?\?+$/i.test(raw) || (/non\s*pay/i.test(raw) && /\?/.test(raw))) {
        return 'Non payé';
    }
    if (/partiellement\s*pay\?+/i.test(raw)) {
        return 'Partiellement payé';
    }
    if (/^Cr\?+e$/i.test(raw) || /^Cr.{0,2}\?+.{0,2}e$/i.test(raw)) {
        return 'Crée';
    }
    if (/^Annul\?+/i.test(raw)) {
        return 'Annulé';
    }
    if (/^Pay\?+/i.test(raw) && !/non/i.test(raw)) {
        return 'Payé';
    }
    if (/[├ÂÃ]/.test(raw)) {
        try {
            const bytes = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) {
                bytes[i] = raw.charCodeAt(i) & 0xff;
            }
            const decoded = new TextDecoder('utf-8').decode(bytes);
            if (decoded && !decoded.includes('\uFFFD')) {
                return decoded.trim();
            }
        } catch {
            /* ignore */
        }
        return raw
            .replace(/pay├®/gi, 'payé')
            .replace(/Pay├®/g, 'Payé')
            .replace(/├®/g, 'é')
            .replace(/Ã©/g, 'é')
            .replace(/Ã¨/g, 'è')
            .replace(/Ã /g, 'à');
    }
    return raw;
}

/** Normalise un titre de statut facture pour comparaison (casse / accents). */
export function normalizeFactureStatutTitre(status: string): string {
    const repaired = repairStatutTitreEncoding(status);
    const s = String(repaired || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\uFFFD+/g, '')
        .replace(/\?+/g, '')
        .trim()
        .replace(/\s+/g, ' ');
    const map: Record<string, string> = {
        brouillon: 'Brouillon',
        cree: 'Crée',
        paye: 'Payé',
        payee: 'Payé',
        'non paye': 'Non payé',
        'non pay': 'Non payé',
        'partiellement paye': 'Partiellement payé',
        'partiellement pay': 'Partiellement payé',
        annule: 'Annulé',
        annulee: 'Annulé',
    };
    return map[s] || String(repaired || '').trim();
}

/** Libellés canoniques par statut_id (UTF-8 correct, indépendant du titre API). */
export const STATUT_ID_TO_TITRE: Record<number, string> = {
    1: 'Brouillon',
    3: 'Accepté',
    5: 'Payé',
    6: 'Annulé',
    8: 'Négociation',
    9: 'Crée',
    10: 'Valide',
    11: 'Livré',
    12: 'Non payé',
    13: 'Fermé',
    14: 'Reçu',
    15: 'Partiellement payé',
    16: 'En cours',
    17: 'Transféré',
    18: 'Utilisé',
    19: 'Non utilisé',
    20: 'Partiellement utilisé',
};

export function normalizeDocumentStatutTitre(status: string): string {
    const repaired = repairStatutTitreEncoding(status);
    const s = String(repaired || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\uFFFD+/g, '')
        .replace(/\?+/g, '')
        .trim()
        .replace(/\s+/g, ' ');
    const map: Record<string, string> = {
        brouillon: 'Brouillon',
        cree: 'Crée',
        valide: 'Valide',
        annule: 'Annulé',
        annulee: 'Annulé',
        negociation: 'Négociation',
        accepte: 'Accepté',
        paye: 'Payé',
        payee: 'Payé',
        'non paye': 'Non payé',
        'non pay': 'Non payé',
        nonpaye: 'Non payé',
        'partiellement paye': 'Partiellement payé',
        'partiellement pay': 'Partiellement payé',
        partpaye: 'Partiellement payé',
        livre: 'Livré',
        recu: 'Reçu',
        ferme: 'Fermé',
        'non utilise': 'Non utilisé',
        'partiellement utilise': 'Partiellement utilisé',
        utilise: 'Utilisé',
        'en cours': 'En cours',
        transfere: 'Transféré',
    };
    return map[s] || String(repaired || '').trim();
}

/** Résout le libellé affiché d’un statut document (priorité statut_id, puis réparation UTF-8). */
export function resolveDocumentStatutLabel(statut: any, statutId?: number | null): string {
    const sid = Number(
        statutId ??
            statut?.id ??
            (Array.isArray(statut) && statut.length > 0 ? statut[0]?.id : null)
    );
    if (Number.isFinite(sid) && STATUT_ID_TO_TITRE[sid]) {
        return STATUT_ID_TO_TITRE[sid];
    }

    let titre = '';
    if (Array.isArray(statut) && statut.length > 0) {
        titre = statut[0].titre || statut[0].nom || statut[0].libelle || '';
    } else if (typeof statut === 'object' && statut) {
        titre = statut.titre || statut.nom || statut.libelle || '';
    } else if (typeof statut === 'string') {
        titre = statut;
    }

    titre = repairStatutTitreEncoding(String(titre || '').trim());
    if (!titre) {
        return '--';
    }
    return normalizeDocumentStatutTitre(titre);
}

const STATUT_ID_TO_FACTURE_TITRE: Record<number, string> = {
    1: 'Brouillon',
    5: 'Payé',
    6: 'Annulé',
    9: 'Crée',
    12: 'Non payé',
    15: 'Partiellement payé',
};

export function resolveFactureStatutTitre(d: any): string {
    const raw = d?.fullData ?? d;
    const sid = Number(raw?.statut_id ?? d?.statut_id);
    if (Number.isFinite(sid) && STATUT_ID_TO_FACTURE_TITRE[sid]) {
        return STATUT_ID_TO_FACTURE_TITRE[sid];
    }
    let status = repairStatutTitreEncoding(
        (raw?.statut?.titre ?? d?.status ?? raw?.status ?? '').toString().trim()
    );
    if (!status || status === 'Inconnu') {
        return status || '—';
    }
    return normalizeFactureStatutTitre(status);
}

export function isAvoirTableRow(d: any): boolean {
    const raw = d?.fullData ?? d;
    const typeTitre = (
        raw?.typeDocument?.titre ||
        raw?.type_document?.titre ||
        d?.typeDocument?.titre ||
        ''
    )
        .toString()
        .toUpperCase()
        .trim();
    if (typeTitre.includes('AVOIR')) {
        return true;
    }
    if (isStatutMarqueAvoir(raw?.statut)) {
        return true;
    }
    const status = (raw?.statut?.titre ?? d?.status ?? raw?.status ?? '').toString().trim();
    if (LEGACY_AVOIR_TITRES.includes(status)) {
        return true;
    }
    return false;
}

/** Facture « pure » : statut métier facture et pas une ligne avoir. */
export function isFactureTableRowExcludingAvoirs(d: any, factureStatuses: string[]): boolean {
    if (isAvoirTableRow(d)) {
        return false;
    }
    const status = resolveFactureStatutTitre(d);
    if (!status) {
        return false;
    }
    const allowed = (factureStatuses || []).map((s) => normalizeFactureStatutTitre(s));
    if (allowed.includes(status)) {
        return true;
    }
    // Repli : type FACTURE + statut_id connu (évite tableau vide alors que total_items API > 0)
    const raw = d?.fullData ?? d;
    const typeTitre = (raw?.typeDocument?.titre || '').toString().toUpperCase();
    const typeId = Number(raw?.type_document_id ?? raw?.typeDocument?.id);
    const isFactureType =
        typeTitre === 'FACTURE' ||
        typeTitre === 'FACTURE FOURNISSEUR' ||
        typeId === 11 ||
        typeId === 14;
    const sid = Number(raw?.statut_id ?? d?.statut_id);
    return isFactureType && Number.isFinite(sid) && !!STATUT_ID_TO_FACTURE_TITRE[sid];
}
